/**
 * /dashboard/posh/reports/$id — Annual report detail.
 *
 * GET /posh/reports/{id}. NOT 2FA-gated. COUNTS-ONLY stats (POSH Act
 * s.21). HR-only (the backend has refused everyone else since
 * 2026-10-25): anyone else gets the HR-only pointer and the query is
 * never sent. Status transitions follow the report state machine.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/posh/reports/[id]/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link Link → @tanstack/react-router Link
 */

import { useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, Download, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { poshApi, toApiError } from "@/lib/api";
import {
  useReport,
  useUpdateReport,
  useUpdateReportStatus,
} from "@/hooks/use-posh";
import { fmtDate } from "@/lib/posh-format";
import {
  reportFilingBlock,
  reportIsInterim,
  reportStatRows,
} from "@/lib/posh-report";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { usePoshAdmin } from "@/components/posh/use-posh-admin";
import { PoshHrOnlyNotice } from "@/components/posh/posh-hr-only-notice";
import { REPORTS_HR_ONLY_TEXT } from "@/components/posh/posh-policy-errors";
import { Button, buttonVariants } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import type { AnnualReport, AnnualReportData } from "@/types/posh";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/posh/reports/$id",
)({
  component: ReportDetailRoute,
});

// Mirrors models.reportTransitions (draft→generated→filed; terminal
// filed). Only the single legal next move is offered.
const REPORT_NEXT: Record<string, { value: string; label: string } | null> =
  {
    draft: { value: "generated", label: "Mark Generated" },
    generated: { value: "filed", label: "Mark Filed" },
    filed: null,
  };

function reportData(r: AnnualReport): AnnualReportData | null {
  const d: AnnualReportData | undefined = r.report_data;
  return d && typeof d === "object" ? d : null;
}

function ReportDetailRoute() {
  const isAdmin = usePoshAdmin();
  if (!isAdmin) {
    return (
      <PoshModuleShell title="Annual Report">
        <PoshFeatureGate>
          <PoshHrOnlyNotice text={REPORTS_HR_ONLY_TEXT} />
        </PoshFeatureGate>
      </PoshModuleShell>
    );
  }
  return <ReportDetail />;
}

function ReportDetail() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const q = useReport(numId);
  const isAdmin = usePoshAdmin();
  const notesMut = useUpdateReport(numId);
  const statusMut = useUpdateReportStatus(numId);
  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  const [busyDownload, setBusyDownload] = useState<
    "pdf" | "shebox" | "board" | null
  >(null);
  const pdfBusy = busyDownload === "pdf";
  const [fileOpen, setFileOpen] = useState(false);
  const notes = notesDraft ?? q.data?.report.notes ?? "";

  // Shared blob-download flow (same pattern as downloadBlob elsewhere).
  // `filename` is a fallback — the server's Content-Disposition names
  // the file when the browser honours it.
  async function downloadExport(
    kind: "pdf" | "shebox" | "board",
    fetcher: (id: number) => Promise<Blob>,
    filename: string,
  ) {
    setBusyDownload(kind);
    try {
      const blob = await fetcher(numId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(toApiError(err).message);
    } finally {
      setBusyDownload(null);
    }
  }

  async function onDownload() {
    const year = q.data?.report.year ?? numId;
    await downloadExport(
      "pdf",
      poshApi.downloadReportPdf,
      `workright-posh-annual-report-${year}.pdf`,
    );
  }

  async function onSaveNotes() {
    try {
      await notesMut.mutateAsync({ notes });
      toast.success("Notes saved.");
      setNotesDraft(null);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  async function onAdvance(next: string) {
    try {
      await statusMut.mutateAsync({ status: next });
      toast.success("Report status updated.");
      setFileOpen(false);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <PoshModuleShell
      title="Annual Report"
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/posh/reports" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      <PoshFeatureGate>
        {q.isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}

      {q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this report</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {q.data &&
        (() => {
          const r = q.data.report;
          const data = reportData(r);
          const stats = data?.stats ?? null;
          const next = REPORT_NEXT[r.status];
          const isFiled = r.status === "filed";
          // An interim report (generated mid-year) is not the §21 report:
          // labelled as such, and "Mark filed" waits for the year to end
          // and a full-year regeneration (the server refuses it too).
          const interim = reportIsInterim(r);
          const filingBlock = reportFilingBlock(r);
          const statRows = reportStatRows(data, interim);
          return (
            <div className="space-y-5">
              <Card>
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">
                        Annual Report {r.year}
                      </CardTitle>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {data?.reporting_period ??
                          `01 Jan ${r.year} – 31 Dec ${r.year}`}
                        {r.filing_deadline
                          ? ` · Filing deadline ${fmtDate(r.filing_deadline)}`
                          : ""}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                      {interim && (
                        <StatusBadge
                          tone="warning"
                          label={
                            data?.is_interim
                              ? "Interim (Year in Progress)"
                              : "Interim (Generated Before the Year Ended)"
                          }
                        />
                      )}
                      <StatusBadge status={r.status} />
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        disabled={busyDownload !== null}
                        onClick={onDownload}
                      >
                        {pdfBusy ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        PDF
                      </Button>
                      {isAdmin && (
                        <>
                          {/* Rule 14 / SHe-Box submission pack. Server-
                              side: HR-admin RBAC + STRICT 2FA (it carries
                              the ICC member registry) — a non-enrolled
                              admin gets the client's totp_required
                              handling. */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1.5"
                            disabled={busyDownload !== null}
                            onClick={() =>
                              void downloadExport(
                                "shebox",
                                poshApi.downloadReportSheBoxCsv,
                                `workright-posh-shebox-${r.year}.csv`,
                              )
                            }
                          >
                            {busyDownload === "shebox" ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                            SHe-Box Export (CSV)
                          </Button>
                          {/* Companies (Accounts) Rules Rule 8(5)(x)+(xi)
                              board-report annexure draft (counts +
                              compliance statements only). */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1.5"
                            disabled={busyDownload !== null}
                            onClick={() =>
                              void downloadExport(
                                "board",
                                poshApi.downloadReportBoardAnnexureCsv,
                                `workright-board-annexure-${r.year}.csv`,
                              )
                            }
                          >
                            {busyDownload === "board" ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                            Board-Report Annexure (CSV)
                          </Button>
                        </>
                      )}
                      {isAdmin && next && next.value !== "filed" && (
                        <Button
                          size="sm"
                          className="gap-1.5"
                          disabled={statusMut.isPending}
                          onClick={() => onAdvance(next.value)}
                        >
                          {statusMut.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                          {next.label}
                        </Button>
                      )}
                      {isAdmin && next && next.value === "filed" && filingBlock && (
                        <Button
                          size="sm"
                          className="gap-1.5"
                          disabled
                          title={filingBlock}
                        >
                          <Send className="h-4 w-4" />
                          {next.label}
                        </Button>
                      )}
                      {isAdmin && next && next.value === "filed" && !filingBlock && (
                        <AlertDialog
                          open={fileOpen}
                          onOpenChange={setFileOpen}
                        >
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              className="gap-1.5"
                              disabled={statusMut.isPending}
                            >
                              {statusMut.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Send className="h-4 w-4" />
                              )}
                              {next.label}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="sm:max-w-md">
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Mark this report filed?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Marking the report filed makes it immutable —
                                you can&apos;t edit or re-file it afterwards.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            {/* Read-only preview of what's being frozen.
                                Reuses the already-loaded query data (r.year,
                                data.reporting_period, stats.*) — no new
                                fetches. POSH s.21 is counts-only, so there
                                is no monetary field to format here. */}
                            <div className="rounded-md border border-input bg-muted/40 px-3 py-3 text-sm">
                              <p className="mb-2 font-medium text-foreground">
                                Freezing report {r.year}
                                {data?.reporting_period
                                  ? ` · ${data.reporting_period}`
                                  : ""}
                              </p>
                              {stats ? (
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                                  <dt className="text-muted-foreground">
                                    Complaints Received
                                  </dt>
                                  <dd className="text-right font-semibold text-foreground">
                                    {stats.complaints_received}
                                  </dd>
                                  <dt className="text-muted-foreground">
                                    Cases Resolved
                                  </dt>
                                  <dd className="text-right font-semibold text-foreground">
                                    {stats.cases_resolved}
                                  </dd>
                                  <dt className="text-muted-foreground">
                                    Cases Pending
                                  </dt>
                                  <dd className="text-right font-semibold text-foreground">
                                    {stats.complaints_pending}
                                  </dd>
                                  <dt className="text-muted-foreground">
                                    Training Completions
                                  </dt>
                                  <dd className="text-right font-semibold text-foreground">
                                    {stats.training_completed}
                                  </dd>
                                </dl>
                              ) : (
                                <p className="text-muted-foreground">
                                  Report aggregate is not available.
                                </p>
                              )}
                            </div>
                            <AlertDialogFooter>
                              <AlertDialogCancel
                                disabled={statusMut.isPending}
                              >
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction
                                // Keep the dialog open while the mutation
                                // runs; onAdvance closes it on success.
                                onClick={(e) => {
                                  e.preventDefault();
                                  void onAdvance(next.value);
                                }}
                                disabled={statusMut.isPending}
                                className={cn(
                                  buttonVariants({ variant: "destructive" }),
                                  "gap-1.5",
                                )}
                              >
                                {statusMut.isPending && (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                )}
                                {next.label}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                  {isAdmin && next?.value === "filed" && filingBlock && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {filingBlock}
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  {stats ? (
                    <dl className="grid gap-3 sm:grid-cols-2">
                      {statRows.map((row) => (
                        <div
                          key={row.key}
                          className="flex items-center justify-between rounded-md border border-input px-3 py-2"
                        >
                          <dt className="text-sm text-muted-foreground">
                            {row.label}
                          </dt>
                          <dd className="text-sm font-semibold text-gray-900">
                            {stats[row.key]}
                          </dd>
                        </div>
                      ))}
                      <div className="flex items-center justify-between rounded-md border border-input px-3 py-2 sm:col-span-2">
                        <dt className="text-sm text-muted-foreground">
                          Training coverage — employees who completed every
                          mandatory module
                          {stats.employees_fully_trained != null
                            ? ` (${stats.employees_fully_trained} of ${stats.total_employees})`
                            : ""}
                        </dt>
                        <dd className="text-sm font-semibold text-gray-900">
                          {data?.training_coverage_pct ?? 0}%
                        </dd>
                      </div>
                      {interim && (
                        <p className="text-xs text-amber-800 sm:col-span-2">
                          Interim: counts up to{" "}
                          {fmtDate(data?.as_of || r.generated_at || r.created_at)}{" "}
                          — this is not the §21 annual report yet. Generate
                          the report for {r.year} again after 31 December to
                          refresh it.
                        </p>
                      )}
                    </dl>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Report aggregate is not available.
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Notes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isAdmin && !isFiled ? (
                    <>
                      <Label className="sr-only">Notes</Label>
                      <Textarea
                        rows={4}
                        value={notes}
                        onChange={(e) => setNotesDraft(e.target.value)}
                        placeholder="Internal notes for this report (not part of the counts)."
                      />
                      <Button variant="outline"
                        size="sm"
                        disabled={notesMut.isPending}
                        onClick={onSaveNotes}
                        className="gap-1.5"
                      >
                        {notesMut.isPending && (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        )}
                        Save Notes
                      </Button>
                    </>
                  ) : (
                    <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                      {r.notes ||
                        (isFiled
                          ? "This report is filed and immutable."
                          : "No notes.")}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          );
        })()}
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
